/**
 * Carga un frame DICOM extrayendo un rango del study.zip (ZIP STORE).
 * No reutiliza fetchDicomBytes (resume parcial de un .dcm suelto).
 * Fallos de cuota IDB no deben impedir el decode en RAM.
 * También soporta fetch de bloque coalesced (varios Part-10 en un Range).
 */
import cornerstone from 'cornerstone-core'
import {
  DICOM_OFFLINE_CACHE_ENABLED,
  getCachedInstance,
  toArrayBuffer,
  isQuotaLikeError,
} from '../db/dicomCache'
import { enqueuePutComplete } from '../db/dicomCacheWriteQueue'
import {
  imageIdFromBuffer,
  parseSopFromDicomBuffer,
  sopFromManifestInstance,
  fallbackCacheKey,
  loadDicomInstanceWithCache,
  loadDicomInstanceDirectUrl,
} from './loadDicomInstanceWithCache'
import { ensureImageRender } from './ensureImageRender'
import { slicePart10FromBlock } from './zipRangeCoalesce'
import {
  isNonImageSeriesInst,
  isPdfDocumentSeriesInst,
} from './isNonImageInstance'

/**
 * @param {ArrayBuffer} buffer
 * @returns {Promise<{ image: object, imageId: string }>}
 */
async function decodeBuffer(buffer) {
  const imageId = imageIdFromBuffer(buffer)
  try {
    let image = await cornerstone.loadAndCacheImage(imageId)
    image = ensureImageRender(image, { renderKind: 'dicom' })
    return { image, imageId }
  } catch (e) {
    try {
      if (cornerstone.imageCache && typeof cornerstone.imageCache.purgeCache === 'function') {
        cornerstone.imageCache.purgeCache()
      }
    } catch (e2) {
      /* ignore */
    }
    let image = await cornerstone.loadAndCacheImage(imageId)
    image = ensureImageRender(image, { renderKind: 'dicom' })
    return { image, imageId }
  }
}

/**
 * Decode Part-10 + opcional put IDB (path compartido legacy/coalescing).
 * PR/GSPS/SR: no llama a Cornerstone (sin Pixel Data).
 * @param {object} opts
 * @param {ArrayBuffer} opts.buffer
 * @param {object} [opts.inst]
 * @param {object} [opts.series]
 * @param {string} [opts.studyInstanceUID]
 * @param {string} [opts.absoluteUrlTag]
 * @param {boolean} [opts.skipIdbWrite]
 * @param {boolean} [opts.fromCache]
 * @returns {Promise<{ image: object|null, imageId: string, sopInstanceUID: string|null, fromCache: boolean, absoluteUrl: string, loadKind: string, renderKind?: string, nonImage?: boolean, data?: Uint8Array }>}
 */
export async function decodeAndCachePart10Buffer(opts) {
  const {
    buffer,
    inst,
    series,
    studyInstanceUID,
    absoluteUrlTag,
    skipIdbWrite = false,
    fromCache = false,
  } = opts || {}
  if (!buffer || !(buffer instanceof ArrayBuffer) || buffer.byteLength === 0) {
    throw new Error('Buffer DICOM vacío')
  }
  const studyUid = String(studyInstanceUID || '')
  const manifestSop = sopFromManifestInstance(inst)
  const cacheKey =
    absoluteUrlTag != null
      ? fallbackCacheKey(absoluteUrlTag, inst)
      : fallbackCacheKey('part10', inst)
  const sop = manifestSop || parseSopFromDicomBuffer(buffer) || cacheKey

  const isPdf = isPdfDocumentSeriesInst(series || null, inst || null)
  const nonImage = isPdf || isNonImageSeriesInst(series || null, inst || null)
  if (nonImage) {
    if (DICOM_OFFLINE_CACHE_ENABLED && sop && !skipIdbWrite && !fromCache) {
      try {
        enqueuePutComplete({
          sopInstanceUID: sop,
          studyInstanceUID: studyUid,
          blob: buffer,
          expectedByteLength: buffer.byteLength,
        })
      } catch (e) {
        /* ignore */
      }
    }
    const kind = isPdf ? 'pdf' : 'nonimage'
    return {
      image: null,
      imageId: isPdf ? `udv-pdf:${sop}` : `udv-nonimage:${sop}`,
      sopInstanceUID: sop,
      fromCache: !!fromCache,
      absoluteUrl: absoluteUrlTag || `part10:${sop}`,
      loadKind: kind,
      renderKind: kind,
      nonImage: true,
      isPdfDocument: isPdf,
      data: new Uint8Array(buffer),
    }
  }

  const { image, imageId } = await decodeBuffer(buffer)

  if (DICOM_OFFLINE_CACHE_ENABLED && sop && !skipIdbWrite && !fromCache) {
    try {
      enqueuePutComplete({
        sopInstanceUID: sop,
        studyInstanceUID: studyUid,
        blob: buffer,
        expectedByteLength: buffer.byteLength,
      })
    } catch (e) {
      /* ignore */
    }
  }

  return {
    image,
    imageId,
    sopInstanceUID: sop,
    fromCache: !!fromCache,
    absoluteUrl: absoluteUrlTag || `part10:${sop}`,
    loadKind: 'dicom',
  }
}

/**
 * Fetch HTTP Range de un bloque del study.zip.
 * @param {{
 *   archiveUrl: string,
 *   rangeStart: number,
 *   rangeEnd: number,
 *   fetchHeaders?: Record<string,string>,
 *   signal?: AbortSignal,
 * }} opts
 * @returns {Promise<ArrayBuffer>}
 */
export async function fetchZipRangeBlock(opts) {
  const archiveUrl = opts && opts.archiveUrl
  const rangeStart = Math.floor(Number(opts && opts.rangeStart))
  const rangeEnd = Math.floor(Number(opts && opts.rangeEnd))
  if (
    !archiveUrl ||
    !Number.isFinite(rangeStart) ||
    rangeStart < 0 ||
    !Number.isFinite(rangeEnd) ||
    rangeEnd < rangeStart
  ) {
    throw new Error('RANGE_ZIP block: archiveUrl/range inválidos')
  }
  const headers = {
    ...((opts && opts.fetchHeaders) || {}),
    Range: `bytes=${rangeStart}-${rangeEnd}`,
  }
  const fetchOpts = {
    method: 'GET',
    credentials: 'omit',
    headers,
  }
  if (opts && opts.signal) fetchOpts.signal = opts.signal
  const res = await fetch(String(archiveUrl), fetchOpts)
  if (!res.ok && res.status !== 206) {
    throw new Error(`HTTP ${res.status} al leer range block del zip`)
  }
  const buffer = await res.arrayBuffer()
  if (!buffer || buffer.byteLength === 0) {
    throw new Error('Buffer ZIP block vacío')
  }
  return buffer
}

/**
 * Intenta cargar Part-10 desde IDB por SOP del manifest.
 * @returns {Promise<object|null>}
 */
export async function tryLoadPart10FromIdb(inst, studyInstanceUID, series) {
  if (!DICOM_OFFLINE_CACHE_ENABLED) return null
  const manifestSop = sopFromManifestInstance(inst)
  if (!manifestSop) return null
  try {
    const row = await getCachedInstance(manifestSop)
    if (!row || row.status !== 'complete' || !row.blob) return null
    const buffer = await toArrayBuffer(row.blob)
    if (!buffer || buffer.byteLength === 0) return null
    return decodeAndCachePart10Buffer({
      buffer,
      inst,
      series,
      studyInstanceUID,
      absoluteUrlTag: `idb:${manifestSop}`,
      skipIdbWrite: true,
      fromCache: true,
    })
  } catch (e) {
    if (!isQuotaLikeError(e)) {
      console.warn('tryLoadPart10FromIdb', e && e.message)
    }
    return null
  }
}

/**
 * Decode members de un bloque coalesced ya descargado.
 * @param {ArrayBuffer} blockBuffer
 * @param {Array<{ slot: number, localStart: number, byteLength: number, byteOffset: number }>} members
 * @param {{
 *   getInst: (slot: number) => object,
 *   studyInstanceUID?: string,
 *   archiveUrl: string,
 *   skipIdbWrite?: boolean,
 * }} ctx
 * @returns {Promise<Array<{ slot: number, loaded: object|null, error: Error|null }>>}
 */
export async function decodeMembersFromZipBlock(blockBuffer, members, ctx) {
  const out = []
  const list = Array.isArray(members) ? members : []
  for (let i = 0; i < list.length; i++) {
    const m = list[i]
    try {
      const part = slicePart10FromBlock(blockBuffer, m)
      if (!part || part.byteLength === 0) {
        throw new Error('slice Part-10 vacío')
      }
      const tag = `${ctx.archiveUrl}#bytes=${m.byteOffset}-${m.byteOffset + m.byteLength - 1}`
      const loaded = await decodeAndCachePart10Buffer({
        buffer: part,
        inst: ctx.getInst(m.slot),
        studyInstanceUID: ctx.studyInstanceUID,
        absoluteUrlTag: tag,
        skipIdbWrite: !!ctx.skipIdbWrite,
        fromCache: false,
      })
      out.push({ slot: m.slot, loaded, error: null })
    } catch (e) {
      out.push({
        slot: m.slot,
        loaded: null,
        error: e instanceof Error ? e : new Error(String(e)),
      })
    }
  }
  return out
}

/**
 * @param {object} opts
 * @param {string} opts.archiveUrl
 * @param {number} opts.byteOffset
 * @param {number} opts.byteLength
 * @param {object} [opts.inst]
 * @param {string} [opts.studyInstanceUID]
 * @param {string} [opts.dicomUrlFallback] https DIRECT si Range falla
 * @param {Record<string,string>} [opts.fetchHeaders]
 * @param {boolean} [opts.skipIdbRead]
 * @param {boolean} [opts.skipIdbWrite]
 * @returns {Promise<{ image: object, imageId: string, sopInstanceUID: string|null, fromCache: boolean, absoluteUrl: string, loadKind: string }>}
 */
export async function loadDicomFromZipRange(opts) {
  const {
    archiveUrl,
    byteOffset,
    byteLength,
    inst,
    series,
    studyInstanceUID,
    dicomUrlFallback,
    fetchHeaders,
    skipIdbRead = false,
    skipIdbWrite = false,
  } = opts || {}

  const offset = Number(byteOffset)
  const length = Number(byteLength)
  if (!archiveUrl || !Number.isFinite(offset) || offset < 0 || !Number.isFinite(length) || length <= 0) {
    throw new Error('RANGE_ZIP: archiveUrl/offset/length inválidos')
  }

  const studyUid = String(studyInstanceUID || '')
  const rangeUrlTag = `${archiveUrl}#bytes=${offset}-${offset + length - 1}`

  if (!skipIdbRead) {
    const cached = await tryLoadPart10FromIdb(inst, studyUid, series)
    if (cached) {
      return { ...cached, absoluteUrl: rangeUrlTag }
    }
  }

  try {
    const buffer = await fetchZipRangeBlock({
      archiveUrl,
      rangeStart: offset,
      rangeEnd: offset + length - 1,
      fetchHeaders,
    })
    if (buffer.byteLength !== Math.floor(length)) {
      console.warn(
        'loadDicomFromZipRange: length mismatch',
        buffer.byteLength,
        length
      )
    }

    return decodeAndCachePart10Buffer({
      buffer,
      inst,
      series,
      studyInstanceUID: studyUid,
      absoluteUrlTag: rangeUrlTag,
      skipIdbWrite,
      fromCache: false,
    })
  } catch (rangeErr) {
    const direct =
      dicomUrlFallback && /^https?:\/\//i.test(String(dicomUrlFallback).trim())
        ? String(dicomUrlFallback).trim()
        : inst && inst.dicomUrl && /^https?:\/\//i.test(String(inst.dicomUrl).trim())
          ? String(inst.dicomUrl).trim()
          : null

    if (!direct) {
      throw rangeErr
    }
    console.warn('loadDicomFromZipRange: fallback DIRECT_URL', rangeErr)
    try {
      const loaded = await loadDicomInstanceWithCache({
        absoluteUrl: direct,
        inst,
        studyInstanceUID: studyUid,
        fetchHeaders,
        skipIdbRead,
        skipIdbWrite,
      })
      return {
        ...loaded,
        absoluteUrl: direct,
        loadKind: 'dicom',
      }
    } catch (e) {
      const directImg = await loadDicomInstanceDirectUrl(direct)
      return {
        ...directImg,
        absoluteUrl: direct,
        loadKind: 'dicom',
      }
    }
  }
}
